import { useEffect } from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import type { Me } from '@crehana/auth';
import { Button } from '@crehana/compass.ui/v2';
import { useTranslation } from '@crehana/i18n';
import { CustomNextImage as Image } from '@crehana/next';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Col, Container, Row } from '@crehana/ui';
import clsx from 'clsx';
import router from 'next/router';

import { imgixLoader } from '@/shared/utils/imgixLoader';

interface LandingProps {
  me?: Me;
  setCurrentSep: React.Dispatch<React.SetStateAction<number>>;
  firstName: string;
  organizationName: string;
  primaryColor?: string | undefined;
}

const IMAGE =
  'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/05940507-1753-443e-a931-1c395dcf304a-frame-627228.png?auto=format&fit=clip';
const AMPLITUDE_SOURCE = 'Onboarding - Directo login';

const Landing = ({
  me,
  setCurrentSep,
  firstName,
  organizationName,
  primaryColor,
}: LandingProps) => {
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const { slug } = router.query as { slug: string };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    if (me) {
      logEvent({
        event: 'Render Onboarding',
        data: {
          ...getSourceEventParams(),
          ...getCommonAmplitudeProperties(me),
          'Organization Slug': slug,
          'Organization Name': organizationName,
          source: AMPLITUDE_SOURCE,
        },
      });
    }
  }, []);

  const next = () => {
    me &&
      logEvent({
        event: 'Onboarding Started',
        data: {
          ...getSourceEventParams(),
          ...getCommonAmplitudeProperties(me),
          'Organization Slug': slug,
        },
      });
    setCurrentSep(2);
  };

  return (
    <Container className="tw-flex tw-items-center tw-flex-grow">
      <Row>
        <Col
          sm={6}
          md={6}
          lg={6}
          className="tw-flex tw-flex-col tw-justify-center"
        >
          <p className="tw-font-h3 tw-mb-2 tw-text-neutral-dark-800 tw-whitespace-pre-line">
            {t('LANDING_TITLE1', { interpolation: { name: firstName } })}
            <span className="tw-text-primary-light-500">
              {t('LANDING_TITLE2')}
            </span>
          </p>
          <p className="tw-font-body3 tw-text-neutral-dark-800 tw-mb-4">
            {t('LANDING_COPY', {
              interpolation: {
                ORG: organizationName,
              },
            })}
          </p>

          <section className="tw-w-full tw-flex tw-justify-start">
            <Button
              size="md"
              type="button"
              variant="primary"
              className={clsx(
                'tw-hidden sm:tw-block',
                primaryColor && `hover:tw-opacity-90`,
              )}
              // Use style because tw don't change bg color dynamically
              style={{ backgroundColor: primaryColor }}
              onClick={() => next()}
            >
              {t('LANDING_START')}
            </Button>
          </section>
        </Col>
        <Col
          className="tw-flex tw-flex-col tw-items-center tw-justify-center"
          sm={6}
          md={6}
          lg={6}
        >
          <Image
            src={IMAGE}
            loader={imgixLoader}
            layout="intrinsic"
            height={529}
            width={563}
            alt="welcome"
            className="zIndex-50"
            loading="lazy"
            quality={100}
          />
          <Button
            size="md"
            type="button"
            variant="primary"
            className={clsx(
              'tw-block sm:tw-hidden tw-my-3.2',
              primaryColor && `hover:tw-opacity-90`,
            )}
            // Use style because tw don't change bg color dynamically
            style={{ backgroundColor: primaryColor }}
            onClick={() => next()}
          >
            {t('LANDING_START')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default Landing;
